import React from 'react';
import styled from 'styled-components';
import {
  Container,
  ColumnContainer,
  TextContainer,
  RedSeparator,
  RegularText,
  SectionHeader
} from './elements';
import FeaturedAsset, { FeaturedAssetPlaceholder } from './FeaturedAsset';
import NewArrivals, { NewArrivalsPlaceholder } from './NewArrivals';
import SaleCard from '../Sales/SaleCard';
import { useQuery } from '@apollo/client';
import { NEXT_SALES, FEATURED_ASSETS, NEW_ARRIVALS } from './queries';
import background from './featuredAssetBackground.png';
import * as colors from 'src/styles/colors';
import SkeletonBlock from 'src/elements/Skeleton';
import {
  MEDIUM_SCREEN_MAX_WIDTH, WIDE_DESKTOP_MIN_WIDTH
} from 'src/styles/breakpoints';


export const LeftColumn = styled.div`
  display: flex;
  align-items: flex-start;

  @media (min-width: 768px) {
    flex: 0.5;
    max-width: 50%;
  }

  @media only screen and (min-device-width: 481px) and (max-device-width: 1180px) and (orientation:portrait) {
    /* For portrait layouts only */
    font-size: 14px;
  }
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;


  @media (min-width: 768px) {
    flex: 0.45;
    max-width: 45%;
    height: fit-content;
  }

  @media (min-width: 768px) {
    /* The image has transparent rounded top corners; the solid color
       behind it squares them off. */
    background-color: ${colors.elRed};
    background-image: url(${background});
    background-repeat: no-repeat;
    background-size: cover;
  }

`;

/* Pull back most of the shared RedSeparator's 40px bottom margin.
   Column count follows the sale count (max 3) but steps down on
   narrower screens so long sale names wrap between words instead of
   breaking mid-word. */
const SaleCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding: 0 5%;
  margin-top: -20px;

  @media (min-width: ${MEDIUM_SCREEN_MAX_WIDTH}px) {
    grid-template-columns: repeat(
      ${({ $columns }) => Math.min($columns, 2)}, 1fr
    );
  }

  @media (min-width: ${WIDE_DESKTOP_MIN_WIDTH}px) {
    grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  }
`;

/* Lines the hero header up with the page's 5% section gutter; the column
   is full-width below 768px and half-width above. */
const HeroTextContainer = styled(TextContainer)`
  margin-left: 5%;

  @media (min-width: 768px) {
    margin-left: 10%;
    /* Matches FeaturedAsset's fixed 25px top padding so both headers
       start level. */
    margin-top: 25px;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 30px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatValue = styled.div`
  color: ${colors.elRed};
  font-size: 22px;
  font-weight: 600;
`;

const StatLabel = styled.div`
  font-size: 13px;
`;

const heroStats = [
  { value: '85+', label: 'Years Of Experience' },
  { value: '1000+', label: 'Inventory Items' },
  { value: '100+', label: 'Successful Auctions' },
  { value: '25+', label: 'Team Members' },
];

const UpcomingAuctionsSection = styled.div`
  background-color: ${colors.elLightGray};
  padding: 20px 0 10px;
`;

const SectionHeaderContainer = styled.div`
  padding: 0 5%;
`;

const HomeHeader = () => {
  const { data, loading: salesLoading } = useQuery(NEXT_SALES);
  const {
    data: featuredData, loading: featuredLoading
  } = useQuery(FEATURED_ASSETS);
  const {
    data: newArrivalsData, loading: newArrivalsLoading
  } = useQuery(NEW_ARRIVALS);
  let sales = data?.sales;
  let featuredAssets = featuredData?.assets;
  let newArrivals = newArrivalsData?.assets;

  return (
    <Container>
      {newArrivalsLoading ?
        <NewArrivalsPlaceholder /> :
        newArrivals && <NewArrivals assets={newArrivals} />
      }
      <ColumnContainer>
        <LeftColumn>
          <HeroTextContainer>
            <SectionHeader>About EquipLinc Auction Group</SectionHeader>
            <RedSeparator />
            <RegularText>
              EquipLinc Auction Group is a full-service heavy truck and
              equipment auction provider. Our team of professionals remarkets
              commercial assets nationwide to a worldwide audience. With over
              85 years of heavy truck and equipment auction experience, we
              look forward to serving all of your commercial asset needs!
            </RegularText>
            <StatGrid>
              {heroStats.map(stat => (
                <div key={stat.label}>
                  <StatValue>{stat.value}</StatValue>
                  <StatLabel>{stat.label}</StatLabel>
                </div>
              ))}
            </StatGrid>
          </HeroTextContainer>
        </LeftColumn>
        {featuredLoading ?
          <RightColumn>
            <FeaturedAssetPlaceholder />
          </RightColumn> :
          featuredAssets?.length ?
            <RightColumn>
              <FeaturedAsset assets={featuredAssets} />
            </RightColumn> :
            null
        }
      </ColumnContainer>
      {salesLoading || sales?.length ?
        <UpcomingAuctionsSection>
        <SectionHeaderContainer>
          <SectionHeader>Upcoming Auctions</SectionHeader>
          <RedSeparator />
        </SectionHeaderContainer>
      <SaleCardGrid $columns={Math.min(sales?.length || 3, 3)}>
        {salesLoading &&
          [...Array(3).keys()].map(index => (
            <SkeletonBlock
              key={index}
              $height='260px'
              $radius='10px'
            />
          ))
        }
        {sales?.map(sale => (
          <SaleCard key={sale.id} sale={sale} />
        ))}
      </SaleCardGrid>
        </UpcomingAuctionsSection> :
        null
      }
    </Container>
  )
}

export default HomeHeader;
